import classNames from "classnames";
import { useFormik } from "formik";
import Image from "next/image";
import { useRouter } from "next/router";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactModal from "react-modal";
import Button from "~/components/Button";
import Icon from "~/components/Icon";
import Input from "~/components/Input";
import Text from "~/components/Text";
import Textarea from "~/components/Textarea";
import Colors from "~/constants/colors";
import { isValidEmail } from "~/helpers/form.helpers";
import { AffiliateApplicationApi } from "~/services/affiliate-application";
import Trans from "~/types/translationKeys";
import styles from "./ApplicationFormModal.module.scss";

let openModalFn: Function;

function ApplicationFormModal() {
  const router = useRouter();

  const [open, setOpen] = useState(false);

  const onClose = () => setOpen(false);

  const openModal = () => setOpen(true);
  useEffect(() => {
    openModalFn = openModal;
  }, []);

  const [error, setError] = useState<string>();
  const { t } = useTranslation();

  const { touched, errors, getFieldProps, submitForm, isSubmitting, dirty } =
    useFormik({
      enableReinitialize: true,
      validateOnBlur: false,
      initialValues: {
        email: "",
        name: "",
        phone_number: "",
        company_website: "",
        workspace_name: "",
        message: "",
      },
      validate: (values) => {
        const err: Record<string, string> = {};
        if (!values.email.trim()) {
          err.email = t(Trans.please_enter_your, {
            name: t(Trans.email).toLowerCase(),
          });
        } else if (!isValidEmail(values.email)) {
          err.email = t(Trans.wrong_email_format);
        }
        return err;
      },
      onSubmit: async ({ email, ...rest }) => {
        try {
          setError("");
          const res = await AffiliateApplicationApi.create({
            email,
            application_data: rest,
          });
          if (res) {
            router.push("/referral/thank-you");
          }
        } catch (err: any) {
          setError(err.message);
        }
      },
    });

  return (
    <ReactModal
      closeTimeoutMS={250}
      isOpen={open}
      onRequestClose={onClose}
      style={{
        content: {},
        overlay: { zIndex: 10000, backgroundColor: "rgba(23, 24, 26, 0.50)" },
      }}
      className={styles.modal}
    >
      <div className={styles.modal_container}>
        <Icon
          name="close"
          className={styles.close_icon}
          onClick={onClose}
          color={Colors.GREY_500}
        />

        <div className={styles.form_header}>
          <Text type="title-2">{t(Trans.modal_referral_title)}</Text>
        </div>

        <div className={styles.form_container}>
          {!!error && (
            <div className="error-box">
              <Text color={Colors.ACCENT_RED}>{error}</Text>
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              label={t(Trans.your, {
                name: t(Trans.name),
              })}
              required
              containerClassName="w-full"
              className="!h-10 !rounded-lg"
              placeholder={t(Trans.enter_your, {
                name: t(Trans.name).toLowerCase(),
              })}
              {...getFieldProps("name")}
              error={touched.name && errors.name}
            />
            <Input
              label={t(Trans.your, {
                name: t(Trans.email),
              })}
              required
              containerClassName="w-full"
              className="!h-10 !rounded-lg"
              placeholder={t(Trans.enter_your, {
                name: t(Trans.email).toLowerCase(),
              })}
              {...getFieldProps("email")}
              error={touched.email && errors.email}
            />
          </div>
          <Input
            label={t(Trans.your, {
              name: t(Trans.contact),
            })}
            containerClassName="w-full"
            className="!h-10 !rounded-lg"
            placeholder={t(Trans.enter_your, {
              name: t(Trans.contact).toLowerCase(),
            })}
            {...getFieldProps("phone_number")}
            error={touched.phone_number && errors.phone_number}
          />
          <Input
            label={t(Trans.your, {
              name: t(Trans.company_website),
            })}
            containerClassName="w-full"
            className="!h-10 !rounded-lg"
            placeholder={"https://..."}
            {...getFieldProps("company_website")}
            error={touched.company_website && errors.company_website}
          />
          <Input
            label={t(Trans.have_you_subscribed_to_any_cx_genie_plan)}
            containerClassName="w-full"
            className="!h-10 !rounded-lg"
            placeholder={t(Trans.enter_your, {
              name: "Workspace Name".toLowerCase(),
            })}
            {...getFieldProps("workspace_name")}
            error={touched.workspace_name && errors.workspace_name}
          />
          <Textarea
            label={t(Trans.tell_us_more_about_you)}
            placeholder={t(Trans.do_you_own_any_group_or_community)}
            {...getFieldProps("message")}
          />

          <div className={styles.buttons_container}>
            <Button
              disabled={isSubmitting}
              onClick={onClose}
              type={"outline"}
              variant={"secondary"}
            >
              {t(Trans.cancel)}
            </Button>
            <Button
              disabled={!dirty}
              loading={isSubmitting}
              onClick={submitForm}
            >
              {t(Trans.submit)}
            </Button>
          </div>
        </div>
      </div>
    </ReactModal>
  );
}

export const openModal = () => {
  openModalFn();
};
export default ApplicationFormModal;
